import {
	MODERATION_CASE_STATUSES,
	MODERATION_SEVERITIES,
	MODERATION_SIGNALS,
	MODERATION_SURFACES,
} from "@repo/api-dtos"
import type {
	ModerationCaseStatus,
	ModerationSeverity,
} from "@/types/marketplace.types"

export const MODERATION_SORT_FIELDS = [
	"priority",
	"severity",
	"lastVerdictAt",
	"openedAt",
] as const
export type ModerationSortField = (typeof MODERATION_SORT_FIELDS)[number]
export type ModerationSortDir = "asc" | "desc"

export const DEFAULT_MODERATION_SORT = "priority:desc"
export const STATUS_ALL = "all"

export interface ModerationSearch {
	page?: number
	search?: string
	pending?: "1"
	severity?: string
	status?: string
	tags?: string
	surface?: string
	userId?: string
	sort?: string
}

const HEX_ID = /^[0-9a-f]{24}$/i

export function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

export function normalizeText(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined
	const trimmed = raw.trim()
	return trimmed.length > 0 ? trimmed : undefined
}

export function normalizePending(raw: unknown): "1" | undefined {
	return raw === "1" || raw === 1 || raw === true ? "1" : undefined
}

/** CSV of values, filtered to the allowed set and de-duplicated; empty → absent. */
export function normalizeCsv(
	raw: unknown,
	allowed: readonly string[],
): string | undefined {
	if (typeof raw !== "string") return undefined
	const values = [...new Set(raw.split(",").map((v) => v.trim()))].filter((v) =>
		allowed.includes(v),
	)
	return values.length > 0 ? values.join(",") : undefined
}

export function normalizeSeverity(raw: unknown): string | undefined {
	return normalizeCsv(raw, MODERATION_SEVERITIES)
}

/** Known statuses as CSV, or the `all` sentinel; anything else is dropped (= unresolved). */
export function normalizeStatus(raw: unknown): string | undefined {
	if (raw === STATUS_ALL) return STATUS_ALL
	return normalizeCsv(raw, MODERATION_CASE_STATUSES)
}

export function normalizeTags(raw: unknown): string | undefined {
	return normalizeCsv(raw, MODERATION_SIGNALS)
}

export function normalizeSurface(raw: unknown): string | undefined {
	return normalizeCsv(raw, MODERATION_SURFACES)
}

export function normalizeUserId(raw: unknown): string | undefined {
	return typeof raw === "string" && HEX_ID.test(raw) ? raw : undefined
}

/** `field:direction`, both whitelisted; the default is left absent. */
export function normalizeSort(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined
	const [field, direction] = raw.split(":")
	const validField = MODERATION_SORT_FIELDS.find((f) => f === field)
	if (!validField) return undefined
	if (direction !== "asc" && direction !== "desc") return undefined
	const sort = `${validField}:${direction}`
	return sort === DEFAULT_MODERATION_SORT ? undefined : sort
}

export function normalizeModerationSearch(
	search: Record<string, unknown>,
): ModerationSearch {
	return {
		page: normalizePage(search.page),
		search: normalizeText(search.search),
		pending: normalizePending(search.pending),
		severity: normalizeSeverity(search.severity),
		status: normalizeStatus(search.status),
		tags: normalizeTags(search.tags),
		surface: normalizeSurface(search.surface),
		userId: normalizeUserId(search.userId),
		sort: normalizeSort(search.sort),
	}
}

export function splitCsv(value: string | undefined): string[] {
	if (!value) return []
	return value.split(",").filter(Boolean)
}

export function parseSeverity(
	value: string | undefined,
): ModerationSeverity[] | undefined {
	const values = splitCsv(value) as ModerationSeverity[]
	return values.length > 0 ? values : undefined
}

/** Statuses the badge and KPI count: everything not resolved. */
export const UNRESOLVED_STATUSES: ModerationCaseStatus[] = ["open", "reviewing"]

/** Absent = unresolved cases; `all` = no status filter. */
export function parseStatus(
	value: string | undefined,
): ModerationCaseStatus[] | undefined {
	if (value === STATUS_ALL) return undefined
	const values = splitCsv(value) as ModerationCaseStatus[]
	return values.length > 0 ? values : UNRESOLVED_STATUSES
}

/** CSV for the URL; the unresolved default folds back to absent, nothing selected means `all`. */
export function serializeStatus(
	values: ModerationCaseStatus[],
): string | undefined {
	if (values.length === 0) return STATUS_ALL
	const isDefault =
		values.length === UNRESOLVED_STATUSES.length &&
		UNRESOLVED_STATUSES.every((s) => values.includes(s))
	return isDefault ? undefined : values.join(",")
}

export function parseSort(value: string | undefined): {
	sortBy: ModerationSortField
	sortDir: ModerationSortDir
} {
	const [by, dir] = (value ?? DEFAULT_MODERATION_SORT).split(":")
	return {
		sortBy: MODERATION_SORT_FIELDS.find((f) => f === by) ?? "priority",
		sortDir: dir === "asc" ? "asc" : "desc",
	}
}

/** Toggles one value inside a CSV param; empty → absent. */
export function toggleCsvValue(
	current: string | undefined,
	value: string,
): string | undefined {
	const values = splitCsv(current)
	const next = values.includes(value)
		? values.filter((v) => v !== value)
		: [...values, value]
	return next.length > 0 ? next.join(",") : undefined
}
